import React, { useState, useMemo } from 'react';
import { 
  X, 
  Bookmark, 
  Clock, 
  Trash2, 
  ArrowRight, 
  ShieldCheck, 
  Lock, 
  Star, 
  ExternalLink,
  Download,
  Activity,
  Filter,
  ArrowUpDown,
  Search,
  SlidersHorizontal,
  Calendar,
  RotateCcw
} from 'lucide-react';
import { SavedCalculation, RegionInfo } from '../../types';
import { authService } from '../../services/auth/authService';
import { TOOLS_DIRECTORY } from '../../data/toolsData';

interface UserWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  savedCalculations: SavedCalculation[];
  onDeleteSaved: (id: string) => void;
  onClearAllSaved: () => void;
  onReopenCalculation: (toolId: string) => void;
  favoriteCurrencies: string[];
  onToggleFavoriteCurrency: (curr: string) => void;
  selectedRegion: RegionInfo;
}

export const UserWorkspaceModal: React.FC<UserWorkspaceModalProps> = ({
  isOpen,
  onClose,
  savedCalculations,
  onDeleteSaved,
  onClearAllSaved,
  onReopenCalculation,
  favoriteCurrencies,
  onToggleFavoriteCurrency,
  selectedRegion,
}) => {
  const [activeTab, setActiveTab] = useState<'saved' | 'recent' | 'favorites' | 'privacy'>('saved');

  // Sorting & Filtering states for Saved Calculations
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [dateSortOrder, setDateSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [searchFilter, setSearchFilter] = useState<string>('');

  if (!isOpen) return null;

  const recentOps = authService.getRecentOperations().slice(0, 5);

  // Helper to resolve an accurate tool category
  const getItemCategory = (item: SavedCalculation): string => {
    if (item.category) return item.category;
    const matchedTool = TOOLS_DIRECTORY.find((t) => t.id === item.toolId || t.slug === item.toolId);
    return matchedTool?.category || 'Everyday Money';
  };

  // Calculate available categories and their counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: savedCalculations.length };
    savedCalculations.forEach((item) => {
      const cat = getItemCategory(item);
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [savedCalculations]);

  const availableCategories = useMemo(() => {
    const defaultOrder = ['Currency & Transfers', 'Income & Tax', 'Borrow', 'Save & Invest', 'Everyday Money'];
    const foundCategories: string[] = Array.from(new Set(savedCalculations.map((item) => getItemCategory(item))));
    const sorted = defaultOrder.filter((cat) => foundCategories.includes(cat) || Boolean(categoryCounts[cat]));
    foundCategories.forEach((cat) => {
      if (!sorted.includes(cat)) sorted.push(cat);
    });
    return sorted;
  }, [savedCalculations, categoryCounts]);

  // Filter and sort saved calculations
  const processedSavedCalculations = useMemo(() => {
    let result = [...savedCalculations];

    // Category filter
    if (categoryFilter !== 'ALL') {
      result = result.filter((item) => getItemCategory(item) === categoryFilter);
    }

    // Search query filter
    if (searchFilter.trim()) {
      const q = searchFilter.toLowerCase().trim();
      result = result.filter((item) => {
        const cat = getItemCategory(item).toLowerCase();
        const title = (item.title || '').toLowerCase();
        const tool = item.toolName.toLowerCase();
        const primary = item.primaryResult.toLowerCase();
        const inputs = (item.inputsSummary || '').toLowerCase();
        const reg = (item.region || '').toLowerCase();
        return (
          title.includes(q) ||
          tool.includes(q) ||
          primary.includes(q) ||
          inputs.includes(q) ||
          reg.includes(q) ||
          cat.includes(q)
        );
      });
    }

    // Date sorting
    result.sort((a, b) => {
      const timeA = a.timestamp || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
      const timeB = b.timestamp || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      return dateSortOrder === 'newest' ? timeB - timeA : timeA - timeB;
    });

    return result;
  }, [savedCalculations, categoryFilter, searchFilter, dateSortOrder]);

  const getCategoryBadgeClass = (category: string) => {
    switch (category) {
      case 'Currency & Transfers':
      case 'Currency':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Income & Tax':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Borrow':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Save & Invest':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Everyday Money':
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const formatItemDate = (timestamp?: number, createdAt?: string | number) => {
    const raw = timestamp || (createdAt ? new Date(createdAt).getTime() : null);
    if (!raw) return 'Saved calculation';
    const d = new Date(raw);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleExportData = () => {
    const dataStr = JSON.stringify({
      savedCalculations,
      favoriteCurrencies,
      exportedAt: new Date().toISOString(),
      platform: 'YamooPay Global Money Intelligence',
    }, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `yamoopay-financial-workspace-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/35 backdrop-blur-xs animate-in fade-in duration-150">
      <div 
        className="w-full max-w-2xl bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[85vh] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-4 sm:p-6 border-b border-[#E5E7EB] flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-[#1A1A1A] text-white flex items-center justify-center font-bold text-xs sm:text-sm shrink-0">
              YP
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-[#1A1A1A] truncate">
                My YamooPay Workspace
              </h2>
              <p className="text-xs text-[#6B7280] line-clamp-1 sm:line-clamp-none">
                Private, offline-ready local state & saved financial scenarios
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6] rounded-xl transition-colors shrink-0 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-[#E5E7EB] px-4 sm:px-6 bg-[#FBFBFC] overflow-x-auto gap-1 sm:gap-2 no-scrollbar">
          <button
            onClick={() => setActiveTab('saved')}
            className={`py-3 px-2.5 sm:px-3 text-xs font-bold border-b-2 whitespace-nowrap transition-colors flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'saved'
                ? 'border-[#1A1A1A] text-[#1A1A1A]'
                : 'border-transparent text-[#6B7280] hover:text-[#1A1A1A]'
            }`}
          >
            <Bookmark className="w-3.5 h-3.5" />
            <span>Saved ({savedCalculations.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('recent')}
            className={`py-3 px-2.5 sm:px-3 text-xs font-bold border-b-2 whitespace-nowrap transition-colors flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'recent'
                ? 'border-[#1A1A1A] text-[#1A1A1A]'
                : 'border-transparent text-[#6B7280] hover:text-[#1A1A1A]'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>Recent ({recentOps.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('favorites')}
            className={`py-3 px-2.5 sm:px-3 text-xs font-bold border-b-2 whitespace-nowrap transition-colors flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'favorites'
                ? 'border-[#1A1A1A] text-[#1A1A1A]'
                : 'border-transparent text-[#6B7280] hover:text-[#1A1A1A]'
            }`}
          >
            <Star className="w-3.5 h-3.5" />
            <span>Currencies ({favoriteCurrencies.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('privacy')}
            className={`py-3 px-2.5 sm:px-3 text-xs font-bold border-b-2 whitespace-nowrap transition-colors flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'privacy'
                ? 'border-[#1A1A1A] text-[#1A1A1A]'
                : 'border-transparent text-[#6B7280] hover:text-[#1A1A1A]'
            }`}
          >
            <Lock className="w-3.5 h-3.5" />
            <span>Privacy & Storage</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 flex-1">
          {/* SAVED TAB */}
          {activeTab === 'saved' && (
            <div className="space-y-4">
              {savedCalculations.length === 0 ? (
                <div className="text-center py-10 space-y-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#F3F4F6] text-[#9CA3AF] flex items-center justify-center mx-auto">
                    <Bookmark className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-[#1A1A1A]">No saved calculations yet</h4>
                    <p className="text-xs text-[#6B7280] max-w-sm mx-auto">
                      Click the "Save" bookmark icon on any calculator result to pin your salary estimates, mortgage payments, or FX scenarios here.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Category Filter Pills */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-[#1A1A1A]">
                        <Filter className="w-3.5 h-3.5 text-[#6B7280]" />
                        <span>Filter by Category:</span>
                      </div>
                      <span className="text-[11px] text-[#6B7280]">
                        Showing {processedSavedCalculations.length} of {savedCalculations.length}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
                      <button
                        onClick={() => setCategoryFilter('ALL')}
                        className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 cursor-pointer ${
                          categoryFilter === 'ALL'
                            ? 'bg-[#1A1A1A] text-white'
                            : 'bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB] hover:text-[#1A1A1A]'
                        }`}
                      >
                        <span>All Categories</span>
                        <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                          categoryFilter === 'ALL' ? 'bg-white/20 text-white' : 'bg-white text-[#6B7280]'
                        }`}>
                          {categoryCounts.ALL || 0}
                        </span>
                      </button>

                      {availableCategories.map((cat) => {
                        const count = categoryCounts[cat] || 0;
                        if (count === 0 && categoryFilter !== cat) return null;
                        return (
                          <button
                            key={cat}
                            onClick={() => setCategoryFilter(cat)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 cursor-pointer ${
                              categoryFilter === cat
                                ? 'bg-[#1A1A1A] text-white'
                                : 'bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB] hover:text-[#1A1A1A]'
                            }`}
                          >
                            <span>{cat}</span>
                            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                              categoryFilter === cat ? 'bg-white/20 text-white' : 'bg-white text-[#6B7280]'
                            }`}>
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Search and Sort Toolbar */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 pt-1 pb-1 border-t border-[#F3F4F6]">
                    {/* Search Input */}
                    <div className="relative flex-1 max-w-sm">
                      <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search saved scenarios..."
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs bg-[#FBFBFC] border border-[#E5E7EB] rounded-lg focus:outline-hidden focus:border-[#1A1A1A] focus:bg-white text-[#1A1A1A] placeholder-[#9CA3AF]"
                      />
                      {searchFilter && (
                        <button
                          onClick={() => setSearchFilter('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#1A1A1A]"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    {/* Sort Selector & Actions */}
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      <div className="flex items-center gap-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-0.5">
                        <span className="text-[10px] font-bold text-[#6B7280] px-2 flex items-center gap-1">
                          <ArrowUpDown className="w-3 h-3" />
                          <span className="hidden sm:inline">Sort:</span>
                        </span>
                        <button
                          onClick={() => setDateSortOrder('newest')}
                          className={`px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                            dateSortOrder === 'newest'
                              ? 'bg-white text-[#1A1A1A] font-bold shadow-2xs'
                              : 'text-[#6B7280] hover:text-[#1A1A1A]'
                          }`}
                          title="Sort by newest date"
                        >
                          Newest
                        </button>
                        <button
                          onClick={() => setDateSortOrder('oldest')}
                          className={`px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                            dateSortOrder === 'oldest'
                              ? 'bg-white text-[#1A1A1A] font-bold shadow-2xs'
                              : 'text-[#6B7280] hover:text-[#1A1A1A]'
                          }`}
                          title="Sort by oldest date"
                        >
                          Oldest
                        </button>
                      </div>

                      <button
                        onClick={onClearAllSaved}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-2 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                        title="Delete all saved calculations"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Clear All</span>
                      </button>
                    </div>
                  </div>

                  {/* Empty Filter State */}
                  {processedSavedCalculations.length === 0 ? (
                    <div className="p-8 text-center bg-[#FBFBFC] rounded-xl border border-dashed border-[#D1D5DB] space-y-2">
                      <p className="text-xs text-[#6B7280]">
                        No saved calculations match the active filter or search query.
                      </p>
                      <button
                        onClick={() => {
                          setCategoryFilter('ALL');
                          setSearchFilter('');
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] hover:bg-[#F3F4F6] text-[#1A1A1A] text-xs font-semibold rounded-lg transition-colors"
                      >
                        <RotateCcw className="w-3 h-3" />
                        <span>Reset Filters</span>
                      </button>
                    </div>
                  ) : (
                    /* Calculations List */
                    <div className="space-y-2.5">
                      {processedSavedCalculations.map((item) => {
                        const category = getItemCategory(item);
                        return (
                          <div
                            key={item.id}
                            className="p-4 bg-[#FBFBFC] hover:bg-white border border-[#E5E7EB] hover:border-[#D1D5DB] hover:shadow-xs rounded-xl flex items-start sm:items-center justify-between gap-4 transition-all"
                          >
                            <div className="space-y-1.5 flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${getCategoryBadgeClass(category)}`}>
                                  {category}
                                </span>
                                <span className="text-xs font-bold text-[#1A1A1A]">{item.toolName}</span>
                                {item.region && (
                                  <span className="text-[10px] font-mono text-[#6B7280] bg-white px-1.5 py-0.5 rounded border border-[#E5E7EB]">
                                    {item.region}
                                  </span>
                                )}
                                <span className="text-[10px] text-[#9CA3AF] flex items-center gap-1 ml-auto sm:ml-0">
                                  <Calendar className="w-3 h-3" />
                                  <span>{formatItemDate(item.timestamp, item.createdAt)}</span>
                                </span>
                              </div>

                              <div className="text-base font-extrabold text-[#1A1A1A] font-mono truncate">
                                {item.primaryResult}
                              </div>

                              {item.inputsSummary && (
                                <div className="text-xs text-[#6B7280] truncate">
                                  {item.inputsSummary}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-2 shrink-0 pt-1 sm:pt-0">
                              <button
                                onClick={() => {
                                  onReopenCalculation(item.toolId);
                                  onClose();
                                }}
                                className="px-3 py-1.5 bg-[#1A1A1A] hover:bg-black text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                                title="Reopen this calculation in the interactive tool"
                              >
                                <span>Reopen</span>
                                <ArrowRight className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => onDeleteSaved(item.id)}
                                className="p-1.5 text-[#9CA3AF] hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                                title="Delete saved scenario"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* RECENT ACTIVITY TAB */}
          {activeTab === 'recent' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs pb-1">
                <div>
                  <h4 className="font-bold text-[#1A1A1A]">Recent Activity & Operations</h4>
                  <p className="text-[11px] text-[#6B7280]">Showing the last 5 calculations, data source syncs, and session events</p>
                </div>
              </div>

              {recentOps.length === 0 ? (
                <div className="text-center py-10 text-xs text-[#6B7280]">No recent activity recorded.</div>
              ) : (
                <div className="space-y-2.5">
                  {recentOps.map((op) => (
                    <div key={op.id} className="p-3.5 bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-black text-white text-[9px] font-bold uppercase rounded font-mono">
                            {op.type}
                          </span>
                          <span className="text-xs font-bold text-[#1A1A1A]">{op.title}</span>
                        </div>
                        <p className="text-xs text-[#4B5563]">{op.details}</p>
                      </div>
                      <span className="text-[10px] font-mono text-[#6B7280] whitespace-nowrap">{op.timestamp}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* FAVORITE CURRENCIES */}
          {activeTab === 'favorites' && (
            <div className="space-y-4">
              <div className="text-xs text-[#6B7280]">
                Quick access currencies pinned to your global rate tables:
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {['USD', 'EUR', 'GBP', 'CAD', 'CHF', 'JPY', 'INR', 'AUD', 'SGD', 'XAF', 'BRL', 'MXN'].map((curr) => {
                  const isFav = favoriteCurrencies.includes(curr);
                  return (
                    <button
                      key={curr}
                      onClick={() => onToggleFavoriteCurrency(curr)}
                      className={`p-3 rounded-xl border text-xs font-bold font-mono flex items-center justify-between transition-all ${
                        isFav
                          ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                          : 'bg-white text-[#1A1A1A] border-[#E5E7EB] hover:bg-[#F3F4F6]'
                      }`}
                    >
                      <span>{curr}</span>
                      <Star className={`w-3.5 h-3.5 ${isFav ? 'fill-amber-400 text-amber-400' : 'text-[#9CA3AF]'}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* PRIVACY & EXPORT */}
          {activeTab === 'privacy' && (
            <div className="space-y-4 text-xs">
              <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-200/80 space-y-2">
                <div className="flex items-center gap-2 text-emerald-950 font-bold">
                  <ShieldCheck className="w-4 h-4 text-emerald-700" />
                  <span>100% Zero-Tracking Financial Privacy</span>
                </div>
                <p className="text-emerald-900 leading-relaxed text-[11px]">
                  YamooPay does not send your entered salaries, loan amounts, or savings figures to remote servers. All computation executes locally inside your web browser.
                </p>
              </div>

              <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3">
                <div className="font-bold text-[#1A1A1A]">Data Portability & Backup</div>
                <p className="text-[#6B7280] text-[11px]">
                  Download a complete JSON export of your saved calculations and preferences.
                </p>
                <button
                  onClick={handleExportData}
                  className="px-3.5 py-2 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] text-[#1A1A1A] text-xs font-bold rounded-xl flex items-center gap-1.5 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Export My Scenarios (.json)</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-[#FBFBFC] border-t border-[#E5E7EB] flex items-center justify-between text-xs text-[#6B7280]">
          <span>Active Jurisdiction: {selectedRegion.name} ({selectedRegion.currency})</span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#1A1A1A] hover:bg-black text-white text-xs font-bold rounded-xl transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
